import { useContext, useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import dateFormat from "dateformat";
import { FileService } from "src/utils/file.service";

import { CourseContext } from "../../../context";

import { TabBar } from "src/components/tabbar";
import { Nav, Tab } from "react-bootstrap";
import { CallAssessmentParameterView } from "src/pages/coaching/components/callAssessmentParameterView";
import DownloadExcel from "src/components/downloadExcel";
import { buildSimulationLearnerReportWorkbook, SIMULATION_OVERALL_SCHEMA } from "src/pages/coaching/manage/simulations/reports/components/detail/simulationLearnerReportExport";

import { fetchLearnerReport } from "src/redux/actions/coaching/simulation";

import { SimulationAttempt } from "src/types/coaching/courses";
import { getFullName } from "src/utils/utils";

import _ from "lodash";
import cx from "classnames"
import styles from "./simulationReportView.module.scss"
import { FactCheckEvaluation } from "src/types/coaching/learners";

interface LearnerDetailsReportProps {
  learner: SimulationAttempt;
}

export const SimulationReportView: React.FC<LearnerDetailsReportProps> = ({
  learner,
}) => {

  const dispatch = useDispatch();

  const courseContext = useContext(CourseContext);

  const simulationReport = courseContext?.simulationReport;
  const closeSimulationReport = courseContext?.closeSimulationReport;
  const callRecording = courseContext?.callRecording;

  const [activeTab, setActiveTab] = useState(0);
  const [selectedAttempt, setSelectedAttempt] = useState(learner);

  const report = useSelector((state: any) =>
    _.get(state.coaching, "simulations.learnerReport.item.report", null),
  );

  const loading = useSelector((state: any) =>
    _.get(state.coaching, "simulations.learnerReport.loading", false),
  );

  const childLearningRecord = useMemo(() => {
    return _.get(simulationReport, "session.childLearningRecord", null);
  }, [simulationReport]);

  const parameters = report?.parameters ?? [];

  useEffect(() => {
    if (learner && childLearningRecord) {
      dispatch(
        fetchLearnerReport({
          id: childLearningRecord,
        })
      );
      setSelectedAttempt(learner);
    }
  }, [learner?.id, childLearningRecord]);

  const attempt = simulationReport?.attempt;
  const session = simulationReport?.session;

  const handleOpenCallRecording = (session: any) => {
    if (!attempt || !session) return;

    courseContext?.openCallRecording?.({
      attempt: attempt,
      session,
    });
  };

  const learnerReportLoading = useSelector((state: any) =>
    _.get(state.coaching, "simulations.learnerReport.loading", false)
  );
  const learnerReportLoaded = useSelector((state: any) =>
    _.get(state.coaching, "simulations.learnerReport.loaded", false)
  );
  const learnerReportError = useSelector((state: any) =>
    _.get(state.coaching, "simulations.learnerReport.error", null)
  );

  const workbook = useMemo(
    () => buildSimulationLearnerReportWorkbook(report),
    [report],
  );

  const overallExportRows = useMemo(() => {
    if (!workbook?.data[0]) return [];
    return workbook.data[0];
  }, [workbook]);

  const exportPagination = useMemo(() => {
    const totalRows = workbook
      ? _.sumBy(workbook.data, (sheet) => _.size(sheet))
      : 1;
    return {
      currentPage: 0,
      totalPages: 1,
      recordsPerPage: Math.max(totalRows, 1),
      totalRecordsCount: Math.max(totalRows, 1),
      hasMoreItems: false,
    };
  }, [workbook]);

  const exportFileName = useMemo(() => {
    const simulationName = report?.simulation ?? "simulation";
    const learnerPart =
      getFullName(selectedAttempt?.user)?.trim() ||
      selectedAttempt?.user?.email ||
      "learner";
    const slug = (s: string) =>
      String(s)
        .replace(/[^a-zA-Z0-9-_]+/g, "_")
        .replace(/^_+|_+$/g, "")
        .slice(0, 48) || "x";
    const attemptPart = String(selectedAttempt?.attemptNumber ?? 1);
    return `${slug(simulationName)}_${slug(learnerPart)}_${attemptPart}`;
  }, [report?.simulation, selectedAttempt?.user, selectedAttempt?.attemptNumber]);
  
  const exportFetchState = {
    loading: learnerReportLoading,
    succeeded: learnerReportLoaded && !learnerReportLoading,
    error: !!learnerReportError,
  };

  if (!simulationReport) return null;

  return (
    <div className={styles.learnerPerformance}>
      <div className={styles.back} onClick={closeSimulationReport}>
        <i className="paperflite-left-arrow"></i>
      </div>
      <div className={styles.header}>
        <div className={styles.titleContainer}>
          <div className={styles.title}>breakdown</div>
          <div className={styles.details}>
            <div className={styles.metadata}>
              call logged on {dateFormat(report?.startedDate, "dd mmm yyyy")},{" "}
              {dateFormat(report?.startedDate, "HH:MM:ss")}
            </div>
            <div className={styles.divider} />
            <div className={styles.metadata}>
              duration {FileService.formatDuration(report?.duration, "short")}
            </div>
          </div>
        </div>
        <div className={styles.actions}>
          {childLearningRecord &&
            report?.status !== "in_sufficient_duration" &&
            _.size(report?.parameters) > 0 && (
              <DownloadExcel
                title="Call Assessment Report"
                fileName={exportFileName}
                schema={SIMULATION_OVERALL_SCHEMA}
                data={overallExportRows}
                multiSheet={Boolean(workbook)}
                multiSheetData={workbook?.data}
                multiSheetSchemas={workbook?.schemas as any}
                multiSheetNames={workbook?.sheets}
                fetchState={exportFetchState}
                pagination={exportPagination}
                fetchDataForPage={() => dispatch(fetchLearnerReport({ id: childLearningRecord }))}
                invalidateData={() => {}}
              />
            )
          }

          {learner?.status === "COMPLETED" &&
            report?.status !== "in_sufficient_duration" && (
              <div className={styles.callRecording}>
                <button className={styles.action} onClick={(e) => {
                  e.stopPropagation();
                  handleOpenCallRecording(session);
                }}>
                  <i className="paperflite-waveform"></i>
                  <span>call recording</span>
                </button>
              </div>
            )}
        </div>
      </div>
      {loading && <ReportPlaceholder />}

      {!loading && report?.status === "in_sufficient_duration" && (
        <div className={styles.emptyMessage}>
          <div className={styles.title}>data unavailable</div>
        </div>
      )}

      {report?.status !== "in_sufficient_duration" && (
        <div className={styles.callAssessmentContainer}>
          <TabBar
            className={cx(styles.tabs)}
            gap={32}
            tabSize="equal"
            active={activeTab}
            onSelectionChange={(index) => setActiveTab(index)}
          >
            {parameters.map((parameter, index) => (
              <span
                key={parameter.uuid}
                className={cx(styles.tab, {
                  [styles.active]: activeTab === index,
                })}
              >
                <div className={styles.name}>
                  {parameter.name} ({Math.round(parameter.score)}/5)
                </div>
              </span>
            ))}
            {!loading &&
              _.size(report?.factChecks) > 0 &&
              _.size(parameters) > 0 && (
                <span
                  key="fact-checker"
                  className={cx(styles.tab, {
                    [styles.active]: activeTab === parameters.length,
                  })}
                >
                  <div className={styles.name}>Claim Validation</div>
                </span>
              )}
          </TabBar>
          <div className={styles.callAssessmentParamater}>
            {parameters.map(
              (parameter, index) =>
                activeTab === index && (
                  <div key={parameter.uuid}>
                    <CallAssessmentParameterView parameter={parameter} />
                  </div>
                ),
            )}
            {_.size(report?.factChecks) > 0 && _.size(parameters) > 0 && activeTab === parameters.length && (
              <CallAssessmentFactInsights
                factChecks={report.factChecks}
              />
            )}
          </div>
        </div>
      )}
    </div>
  );
};

const ReportPlaceholder: React.FC = () => {
  return (
    <div className={cx(styles.callAssessmentContainer, styles.placeholder)}>
      <div className={cx(styles.tabs)}>
        <div className={cx(styles.tab, styles.block)}></div>
        <div className={cx(styles.tab, styles.block)}></div>
        <div className={cx(styles.tab, styles.block)}></div>
        <div className={cx(styles.tab, styles.block)}></div>
        <div className={cx(styles.tab, styles.block)}></div>
        <div className={cx(styles.tab, styles.block)}></div>
      </div>

      <div className={cx(styles.verticalTabs, styles.placeholder)}>
        <span className={cx(styles.text, styles.block)}></span>
        <span className={cx(styles.text, styles.block)}></span>
        <span className={cx(styles.text, styles.block)}></span>
      </div>
    </div>
  );
};

interface CallAssessmentFactInsightsProps {
  factChecks: FactCheckEvaluation[];
}

const CallAssessmentFactInsights: React.FC<CallAssessmentFactInsightsProps> = ({
  factChecks
}) => {
  return (
    <div className={styles.callAssessmentParameterContainer}>

      <div className={styles.parameterTabContainer}>
        <Tab.Container defaultActiveKey="0">

          <Nav className={styles.tabs}>
            {factChecks.map((item, index) => (
              <Nav.Link
                key={index}
                className={styles.tab}
                eventKey={index}
              >
                <span>Claim {index + 1}</span>
              </Nav.Link>
            ))}
          </Nav>

          <Tab.Content className={styles.tabContent}>
            {factChecks.map((item, index) => {

              return (
                <Tab.Pane
                  className={styles.parameterContainer}
                  eventKey={index}
                  key={index}
                >
                  <div className={styles.parameter}>
                    <div className={styles.label}>Rep Statement <span className={styles.timestamp}>{item.timestamp}</span></div>
                    <div className={styles.value}>
                      {item?.claim}
                    </div>
                  </div>

                  {item?.feedback && (
                    <div className={styles.parameter}>
                      <div className={styles.label}>Knowledge Reference</div>
                      <div className={styles.value}>
                        {item?.feedback}
                      </div>
                    </div>
                  )}

                  {/* {evidence?.answer && (
                    <div className={styles.parameter}>
                      <div className={styles.label}>Verified answer</div>

                      <div className={styles.value}>
                        {evidence.answer}
                      </div>

                      {evidence.citations?.length > 0 && (
                        <div className={styles.citations}>
                          {evidence.citations.map((citation, i) => (
                            <div key={i} className={styles.citation}>
                              <span className={styles.citationIndex}>
                                {i + 1}
                              </span>
                              <span className={styles.citationText}>
                                {citation}
                              </span>
                              <i
                                className={cx(
                                  "paperflite-arrow-up",
                                  styles.citationArrow
                                )}
                              />
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  )} */}
                </Tab.Pane>
              );
            })}
          </Tab.Content>
        </Tab.Container>
      </div>
    </div>
  );
};
